import React from 'react';
import ProfileField from './ProfileField';
import { groupFieldsByCategory, getFieldCountByPriority, sortCategoriesByPriority } from '../utils/fieldPriority';

interface ProfileViewerProps {
  profile: any;
  selectedFields?: string[];
  onFieldSelectionChange?: (fields: string[]) => void;
}

/**
 * ProfileViewer Component
 *
 * Displays user profile data with priority-based styling to help experts
 * identify which fields are most critical for equipment recommendations.
 *
 * Priority levels:
 * - M (Mandatory): Green, bold, underlined
 * - GTF (Good to Fill): Orange, bold
 * - O (Optional): Gray, normal
 *
 * Field Selection:
 * - When selectedFields and onFieldSelectionChange are provided, enables field selection mode
 * - Checkboxes appear on each field to select which data shows in rating modals
 */
const ProfileViewer: React.FC<ProfileViewerProps> = ({
  profile,
  selectedFields = [],
  onFieldSelectionChange
}) => {
  const groupedFields = groupFieldsByCategory(profile);
  const categories = Object.keys(groupedFields);
  const sortedCategories = sortCategoriesByPriority(categories, groupedFields);
  const fieldCounts = getFieldCountByPriority(profile);

  const handleToggleField = (fieldKey: string) => {
    if (!onFieldSelectionChange) return;

    const newSelection = selectedFields.includes(fieldKey)
      ? selectedFields.filter(f => f !== fieldKey)
      : [...selectedFields, fieldKey];

    onFieldSelectionChange(newSelection);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      {/* Header with Profile ID */}
      <div className="mb-6 pb-4 border-b-2 border-gray-200">
        <div className="flex items-start justify-between mb-2">
          <h2 className="text-2xl font-bold text-gray-900">
            Profile #{profile.id}
          </h2>

          {/* Field Selection Counter */}
          {onFieldSelectionChange && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                📊 <strong>{selectedFields.length}</strong> field{selectedFields.length !== 1 ? 's' : ''} selected for modal
              </span>
              {selectedFields.length > 0 && (
                <button
                  onClick={() => onFieldSelectionChange([])}
                  className="text-xs text-red-600 hover:text-red-700 hover:underline"
                >
                  Clear All
                </button>
              )}
            </div>
          )}
        </div>

        {/* Quick hint */}
        {onFieldSelectionChange && selectedFields.length === 0 && (
          <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
            💡 <strong>Tip:</strong> Hover over fields below and click checkboxes to select which data shows in the rating modal
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="text-gray-600">
            <strong>Gender:</strong> {profile.gender || 'N/A'}
          </span>
          <span className="text-gray-600">
            <strong>Age:</strong> {profile.age || 'N/A'}
          </span>
          <span className="text-gray-600">
            <strong>Handicap:</strong> {profile.handicap || 'N/A'}
          </span>
          {profile.is_synthetic && (
            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
              Synthetic Profile
            </span>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">🎯 Field Priority Legend</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div className="flex items-center space-x-2 bg-white p-2 rounded border border-green-ink">
            <span className="px-2 py-0.5 bg-green/15 text-green-ink border border-green-ink rounded font-bold">M</span>
            <div>
              <div className="font-semibold text-green-ink">Mandatory</div>
              <div className="text-gray-600">Critical for recommendations</div>
            </div>
          </div>
          <div className="flex items-center space-x-2 bg-white p-2 rounded border border-orange-200">
            <span className="px-2 py-0.5 bg-orange-100 text-orange-600 border border-orange-300 rounded font-bold">GTF</span>
            <div>
              <div className="font-semibold text-orange-600">Good to Fill</div>
              <div className="text-gray-600">Significantly improves quality</div>
            </div>
          </div>
          <div className="flex items-center space-x-2 bg-white p-2 rounded border border-gray-200">
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 border border-gray-300 rounded font-semibold">O</span>
            <div>
              <div className="font-semibold text-gray-700">Optional</div>
              <div className="text-gray-600">Nice to have context</div>
            </div>
          </div>
        </div>
      </div>

      {/* Field Categories */}
      <div className="space-y-6">
        {sortedCategories.map((category) => {
          // Count priority types in this category
          const categoryFields = groupedFields[category];
          const mCount = categoryFields.filter(f => f.priority === 'M').length;
          const gtfCount = categoryFields.filter(f => f.priority === 'GTF').length;
          const oCount = categoryFields.filter(f => f.priority === 'O').length;

          // Determine category highlight color based on highest priority
          let categoryBorderClass = 'border-gray-200';
          let categoryBgClass = 'bg-gray-50';
          if (mCount > 0) {
            categoryBorderClass = 'border-green-ink';
            categoryBgClass = 'bg-green/10/30';
          } else if (gtfCount > 0) {
            categoryBorderClass = 'border-orange-200';
            categoryBgClass = 'bg-orange-50/20';
          }

          return (
            <div key={category} className={`border rounded-lg p-4 ${categoryBorderClass} ${categoryBgClass}`}>
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">
                  {category}
                </h3>
                <div className="flex items-center space-x-2 text-xs">
                  {mCount > 0 && (
                    <span className="px-2 py-0.5 bg-green/15 text-green-ink rounded font-semibold">
                      {mCount} M
                    </span>
                  )}
                  {gtfCount > 0 && (
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-600 rounded font-semibold">
                      {gtfCount} GTF
                    </span>
                  )}
                  {oCount > 0 && (
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                      {oCount} O
                    </span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-1">
                {/* Sort fields within category by priority (M first, then GTF, then O) */}
                {categoryFields
                  .sort((a, b) => {
                    const priorityOrder = { M: 0, GTF: 1, O: 2 };
                    return priorityOrder[a.priority as 'M' | 'GTF' | 'O'] - priorityOrder[b.priority as 'M' | 'GTF' | 'O'];
                  })
                  .map((field) => (
                    <ProfileField
                      key={field.key}
                      label={field.label}
                      value={field.value}
                      priority={field.priority}
                      fieldKey={field.key}
                      isSelected={selectedFields.includes(field.key)}
                      onToggle={handleToggleField}
                    />
                  ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Stats */}
      <div className="mt-6 pt-4 border-t-2 border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">📊 Field Summary</h3>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="bg-green/10 border-2 border-green-ink rounded-lg p-3">
            <div className="text-3xl font-bold text-green-ink">{fieldCounts.M}</div>
            <div className="text-xs text-gray-600 mt-1">Mandatory Fields</div>
          </div>
          <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-3">
            <div className="text-3xl font-bold text-orange-600">{fieldCounts.GTF}</div>
            <div className="text-xs text-gray-600 mt-1">Good to Fill Fields</div>
          </div>
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-3">
            <div className="text-3xl font-bold text-gray-600">{fieldCounts.O}</div>
            <div className="text-xs text-gray-600 mt-1">Optional Fields</div>
          </div>
        </div>
        <div className="mt-3 text-xs text-center text-gray-500">
          Total populated fields: <strong>{fieldCounts.M + fieldCounts.GTF + fieldCounts.O}</strong>
        </div>
      </div>

      {/* Quick Stats Bar - Key Metrics */}
      <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">⚡ Key Metrics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
          <div>
            <div className="text-2xl font-bold text-blue-600">{profile.handicap ?? '?'}</div>
            <div className="text-xs text-gray-600">Handicap</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-ink">{profile.swing_speed_driver ?? '?'}</div>
            <div className="text-xs text-gray-600">Driver Speed (mph)</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-orange-600">{profile.swing_speed_7iron ?? '?'}</div>
            <div className="text-xs text-gray-600">7-Iron Speed (mph)</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-purple-600">{profile.age ?? '?'}</div>
            <div className="text-xs text-gray-600">Age</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileViewer;
